import React, { useEffect, useRef } from "react";
import { Box, Dialog, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

interface ResultModalProps {
  open: boolean;
  action: "APPROVED" | "REJECTED" | "UPDATED";
  onClose: () => void;
}

const ResultModal: React.FC<ResultModalProps> = ({ open, action, onClose }) => {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => onCloseRef.current(), 2000);
    return () => window.clearTimeout(t);
  }, [open]);

  const isSuccess = action !== "REJECTED";
  const fallbackMessage =
    action === "APPROVED"
      ? "Client ID Approved!"
      : action === "REJECTED"
      ? "Client ID Rejected!"
      : "Client ID Updated!";

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // Non-dismissible: ignore backdrop click + escape.
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
          px: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: isSuccess ? "#00CD5B" : "#FF6347",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSuccess ? (
            <CheckIcon sx={{ color: "#fff", fontSize: 44 }} />
          ) : (
            <CloseIcon sx={{ color: "#fff", fontSize: 44 }} />
          )}
        </Box>
        <Typography sx={{ fontWeight: 900, color: "#455A64", mt: 2, fontSize: 18 }}>
          {fallbackMessage}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default ResultModal;

/*
import React, { useEffect } from "react";
import { Box, Dialog, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

interface ResultModalProps {
  open: boolean;
  action: "APPROVED" | "REJECTED";
  onClose: () => void;
}

const ResultModal: React.FC<ResultModalProps> = ({ open, action, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => onClose(), 2000);
    return () => window.clearTimeout(t);
  }, [open, onClose]);

  const isApproved = action === "APPROVED";

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // Non-dismissible: ignore backdrop click + escape.
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
          px: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: isApproved ? "#00CD5B" : "#FF6347",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isApproved ? (
            <CheckIcon sx={{ color: "#fff", fontSize: 44 }} />
          ) : (
            <CloseIcon sx={{ color: "#fff", fontSize: 44 }} />
          )}
        </Box>
        <Typography sx={{ fontWeight: 900, color: "#455A64", mt: 2, fontSize: 18 }}>
          {isApproved ? "Client ID Approved!" : "Client ID Rejected!"}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default ResultModal;

import React, { useEffect } from "react";
import { Box, Dialog, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

interface ResultModalProps {
  open: boolean;
  action: "APPROVED" | "REJECTED";
  onClose: () => void;
}

const ResultModal: React.FC<ResultModalProps> = ({ open, action, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(onClose, 2000);
    return () => clearTimeout(t);
  }, [open, onClose]);

  const isApproved = action === "APPROVED";

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
          px: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: isApproved ? "#00CD5B" : "#FF6347",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isApproved ? (
            <CheckIcon sx={{ color: "#fff", fontSize: 44 }} />
          ) : (
            <CloseIcon sx={{ color: "#fff", fontSize: 44 }} />
          )}
        </Box>
        <Typography
          sx={{
            fontWeight: 900,
            color: "#455A64",
            mt: 2,
            fontSize: 18,
          }}
        >
          {isApproved ? "Client ID Approved!" : "Client ID Rejected!"}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default ResultModal;

import React, { useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";

export type ResultAction = "APPROVED" | "REJECTED";

export interface ResultModalProps {
  open: boolean;
  action: ResultAction;
  onClose: () => void;
  autoDismissMs?: number; // default 2000
}

export default function ResultModal({
  open,
  action,
  onClose,
  autoDismissMs = 2000,
}: ResultModalProps) {
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => onClose(), autoDismissMs);
    return () => window.clearTimeout(t);
  }, [open, onClose, autoDismissMs]);

  const isApproved = action === "APPROVED";
  const circleBg = isApproved ? "#00CD5B" : "#FF6347";
  const titleText = isApproved
    ? "Client ID Approved!"
    : "Client ID Rejected!";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          px: 2,
          pb: 2,
          pt: 1,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: "50%",
            backgroundColor: circleBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isApproved ? (
            <CheckIcon sx={{ color: "#fff", fontSize: 52 }} />
          ) : (
            <CancelIcon sx={{ color: "#fff", fontSize: 52 }} />
          )}
        </Box>
      </Box>

      <Typography
        sx={{
          mt: 2,
          textAlign: "center",
          fontWeight: 800,
          color: "#1F2937",
        }}
      >
        {titleText}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: circleBg,
            color: circleBg,
            borderRadius: "999px",
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
}

*/
